import { FC, Fragment, useEffect, useState } from 'react';
import { Navigate, useMatch } from 'react-router-dom';

interface OrgInviteCaptureProps {
  children: JSX.Element | JSX.Element[];
}

const ORG_TOKEN_INITIAL_HIT = 'organization_token_initial_hit';
const ORG_TOKEN_ON_REDIRECT = 'organization_token_on_redirect';
const ORG_INVITE_PATH = '/join-organization/';

/**
 * Saves organization join token to localstorage if one exists.
 * This is used to return the user to the organization invite page
 * in cases where a new user is going through the registration flow from an org invite link.
 */
export const freezeOrganizationInviteOnRedirect = () => {
  const token = window.localStorage.getItem(ORG_TOKEN_INITIAL_HIT);
  if (token) {
    window.localStorage.setItem(ORG_TOKEN_ON_REDIRECT, token);
    window.localStorage.removeItem(ORG_TOKEN_INITIAL_HIT);
  }
};

const setInitialOrganizationInvite = () => {
  const currentPath = window.location.href;
  const parts = currentPath.split('/');
  const token = parts.pop();
  if (token !== undefined) {
    window.localStorage.setItem(ORG_TOKEN_INITIAL_HIT, token);
  }
};

/** Pass-through wrapper component that captures organization invites into localstorage
 * and makes sure the localstorage is empty in routes other than the org invite page
 */
export const OrganizationInviteCapture: FC<OrgInviteCaptureProps> = ({ children }) => {
  const [tokenOnRedirect, setTokenOnRedirect] = useState<string | null>(null);

  const isOrgInvite = useMatch({
    path: ORG_INVITE_PATH,
  });

  useEffect(() => {
    const token = window.localStorage.getItem(ORG_TOKEN_ON_REDIRECT);
    setTokenOnRedirect(token);
  }, []);

  useEffect(() => {
    if (tokenOnRedirect) {
      window.localStorage.removeItem(ORG_TOKEN_ON_REDIRECT);
      window.localStorage.removeItem(ORG_TOKEN_INITIAL_HIT);
      return;
    }
    if (isOrgInvite) {
      setInitialOrganizationInvite();
    } else {
      window.localStorage.removeItem(ORG_TOKEN_INITIAL_HIT);
    }
  }, [tokenOnRedirect, isOrgInvite]);
  if (tokenOnRedirect) {
    return <Navigate to={`/join-organization/${tokenOnRedirect}`} />;
  }

  return <Fragment>{children}</Fragment>;
};
